import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UsersThree, Plus, PencilSimple, Trash, Crown, LockKey } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { api, formatApiErrorDetail } from "@/lib/api";

export const MODULES = [
  { key: "overview", label: "Overview" },
  { key: "campaigns", label: "Campaigns" },
  { key: "audiences", label: "Audiences" },
  { key: "ads", label: "Ads" },
  { key: "leads", label: "Leads Inbox" },
  { key: "lead_forms", label: "Lead Forms" },
  { key: "assessment", label: "Assessment" },
  { key: "conversion_report", label: "Conversion Report" },
  { key: "ai_agent", label: "AI Agent" },
  { key: "creative_team", label: "Creative Team" },
  { key: "brand_kit", label: "Brand Kit" },
  { key: "insights", label: "Insights" },
  { key: "integrations", label: "Integrations", sensitive: true },
  { key: "billing", label: "Billing", sensitive: true },
];

const PRESETS = {
  full: { label: "Full access", mods: MODULES.filter((m) => !m.sensitive).map((m) => m.key) },
  media_buyer: { label: "Media Buyer", mods: ["overview", "campaigns", "audiences", "ads", "ai_agent", "creative_team", "brand_kit", "insights"] },
  counsellor: { label: "Counsellor", mods: ["overview", "leads", "lead_forms", "assessment"] },
  viewer: { label: "Viewer / Analyst", mods: ["overview", "insights", "conversion_report"] },
  custom: { label: "Custom", mods: null },
};

const MemberDialog = ({ open, onClose, editing, onSaved }) => {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [mods, setMods] = useState([]);
  const [preset, setPreset] = useState("custom");
  const [canPublish, setCanPublish] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ name: editing?.name || "", email: editing?.email || "", password: "" });
      setMods(editing?.modules || []);
      setPreset("custom");
      setCanPublish(!!editing?.can_publish);
    }
  }, [open, editing]);

  const applyPreset = (p) => {
    setPreset(p);
    if (PRESETS[p].mods) setMods(PRESETS[p].mods);
  };

  const toggle = (k) => {
    setPreset("custom");
    setMods((m) => (m.includes(k) ? m.filter((x) => x !== k) : [...m, k]));
  };

  const save = async () => {
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/workspace/users/${editing.id}`, {
          name: form.name, modules: mods, can_publish: canPublish, ...(form.password ? { password: form.password } : {}) });
        toast.success("Member updated");
      } else {
        await api.post("/workspace/users", { ...form, modules: mods, can_publish: canPublish });
        toast.success(`${form.name || form.email} added — share their login: ${form.email}`);
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="member-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-extrabold">{editing ? `Edit ${editing.name}` : "Add team member"}</DialogTitle>
          <DialogDescription className="text-xs">{editing ? "Change their name, password or module access." : "Set their login and tick the modules they can use. Everything else disappears from their app."}<br /><span className="text-[10px] text-[#b07600] font-semibold">By default, publishing to Facebook and executing AI actions stay owner-only — members sign off / recommend. Grant the toggle below only to people you fully trust with ad spend.</span></DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#525866]">Name</Label>
            <Input data-testid="member-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Priya Sharma" className="h-10" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#525866]">Email</Label>
            <Input data-testid="member-email-input" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="priya@company.com" className="h-10" />
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#525866]">{editing ? "New password (leave blank to keep current)" : "Password (min 8 chars)"}</Label>
          <Input data-testid="member-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing ? "••••••••" : "e.g. Counsel@2026"} className="h-10 font-mono" />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#525866]">Role preset</Label>
          <Select value={preset} onValueChange={applyPreset}>
            <SelectTrigger className="h-10 text-xs font-semibold" data-testid="member-preset-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(PRESETS).map(([k, p]) => <SelectItem key={k} value={k}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#525866]">Module access ({mods.length} granted)</Label>
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            {MODULES.map((m) => (
              <label key={m.key} data-testid={`module-check-${m.key}`}
                className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 cursor-pointer transition-colors ${
                  mods.includes(m.key) ? "border-[#0052FF]/40 bg-[#0052FF]/[0.04]" : "border-[#E5E7EB] hover:border-[#c9cdd6]"}`}>
                <Checkbox checked={mods.includes(m.key)} onCheckedChange={() => toggle(m.key)} />
                <span className="text-xs font-semibold text-[#0A0D14]">{m.label}</span>
                {m.sensitive && <LockKey size={11} className="ml-auto text-[#FF6B35]" title="Sensitive — owner area" />}
              </label>
            ))}
          </div>
        </div>
        <label data-testid="member-publish-toggle"
          className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${canPublish ? "border-[#FF6B35]/50 bg-[#FF6B35]/[0.05]" : "border-[#E5E7EB] hover:border-[#c9cdd6]"}`}>
          <Switch checked={canPublish} onCheckedChange={setCanPublish} />
          <span className="min-w-0">
            <span className="block text-xs font-bold text-[#0A0D14]">Can publish & approve <LockKey size={11} className="inline ml-1 -mt-0.5 text-[#FF6B35]" /></span>
            <span className="block text-[10px] text-[#8a90a0]">Lets this member publish creatives to Facebook and approve & execute AI actions — real ad spend. Off = they can only sign off / recommend.</span>
          </span>
        </label>
        <Button onClick={save} disabled={busy || (!editing && (!form.email || !form.password))} data-testid="member-save-button"
          className="w-full h-11 rounded-full bg-[#0052FF] hover:bg-[#0040CC] text-white font-bold">
          {busy ? "Saving…" : editing ? "Save changes" : "Create member"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default function TeamMembers() {
  const [users, setUsers] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = () => api.get("/workspace/users").then(({ data }) => setUsers(data)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);

  const remove = async () => {
    try {
      await api.delete(`/workspace/users/${deleting.id}`);
      toast.success(`${deleting.name} removed`);
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const modLabel = (k) => MODULES.find((m) => m.key === k)?.label || k;

  return (
    <div className="max-w-4xl space-y-6" data-testid="team-members-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-extrabold tracking-tight text-[#0A0D14]">Team Members</h2>
          <p className="text-sm text-[#525866] mt-1">Create logins for your team and control exactly which modules each person can use.</p>
        </div>
        <Button onClick={() => { setEditing(null); setDialog(true); }} data-testid="add-member-button"
          className="rounded-full bg-[#0052FF] hover:bg-[#0040CC] text-white font-bold h-10 px-5">
          <Plus size={15} weight="bold" className="mr-1.5" /> Add member
        </Button>
      </div>

      {!users ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20 rounded-lg" />)}</div>
      ) : (
        <div className="space-y-3">
          {users.map((u) => (
            <div key={u.id} data-testid={`member-row-${u.id}`} className="bg-white border border-[#E5E7EB] rounded-lg p-4 flex items-start gap-4">
              <span className="h-10 w-10 shrink-0 rounded-full bg-[#0A0D14] text-white text-sm font-bold flex items-center justify-center">
                {(u.name || u.email)[0].toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-heading text-sm font-extrabold text-[#0A0D14]">{u.name}</span>
                  {u.role === "owner" ? (
                    <Badge className="bg-[#FF6B35]/10 text-[#c2410c] border border-[#FF6B35]/25 rounded-full font-bold text-[9px] uppercase"><Crown size={10} weight="fill" className="mr-1" /> Owner</Badge>
                  ) : (
                    <Badge className="bg-[#F3F4F6] text-[#525866] border border-[#E5E7EB] rounded-full font-bold text-[9px] uppercase">Member</Badge>
                  )}
                  {u.role !== "owner" && u.can_publish && (
                    <Badge className="bg-[#FF6B35]/10 text-[#c2410c] border border-[#FF6B35]/25 rounded-full font-bold text-[9px] uppercase" data-testid={`publisher-badge-${u.id}`}>Publisher</Badge>
                  )}
                </div>
                <div className="text-xs text-[#8a90a0] mt-0.5">{u.email}</div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {u.role === "owner" ? (
                    <span className="text-[10px] font-bold text-[#8a90a0]">All modules — full control</span>
                  ) : (u.modules || []).map((k) => (
                    <span key={k} className="rounded-full bg-[#0052FF]/[0.06] border border-[#0052FF]/20 text-[#0040CC] px-2 py-0.5 text-[9px] font-bold">{modLabel(k)}</span>
                  ))}
                </div>
              </div>
              {u.role !== "owner" && (
                <div className="flex gap-1.5 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => { setEditing(u); setDialog(true); }} data-testid={`edit-member-${u.id}`}
                    className="rounded-full h-8 px-3 text-[11px] font-bold"><PencilSimple size={12} className="mr-1" /> Edit</Button>
                  <Button variant="outline" size="sm" onClick={() => setDeleting(u)} data-testid={`delete-member-${u.id}`}
                    className="rounded-full h-8 px-3 text-[11px] font-bold border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600"><Trash size={12} /></Button>
                </div>
              )}
            </div>
          ))}
          {users.length === 1 && (
            <div className="rounded-lg border border-dashed border-[#c9cdd6] bg-white p-6 text-center">
              <UsersThree size={22} weight="duotone" className="mx-auto text-[#0052FF]" />
              <p className="text-xs text-[#8a90a0] mt-2">No team members yet. Add a counsellor who only sees leads, or a media buyer who only sees advertising.</p>
            </div>
          )}
        </div>
      )}

      <MemberDialog open={dialog} onClose={() => setDialog(false)} editing={editing} onSaved={load} />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-member-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading font-extrabold text-red-600">Remove {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription className="text-xs">They will lose access immediately. Workspace data is not affected.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full font-bold text-xs" data-testid="delete-member-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="delete-member-confirm" className="rounded-full bg-red-600 hover:bg-red-700 text-white font-bold text-xs">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
